import {
  AdminColorInput,
  AdminDollarInput2,
  AdminSelect,
  AdminSwitch,
  Box,
  Button,
  CustomExpenseEntity,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  lightTheme,
  ModalPortal,
  UpdateCustomExpenseDto,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { ExpenseNameField } from './ExpenseNameField';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Comparable index so month/year pairs can be ordered with a single number. */
const monthIndex = (year: number, month: number) => year * 12 + month;

const monthLabel = (year: number, month: number) =>
  `${MONTHS[month - 1]} ${year}`;

/**
 * Edit the *definition* of a custom expense - PATCH /custom-expenses/:id.
 *
 * Amount and name always take effect from next month onward; already
 * materialized months keep their snapshot, which is why the amount field
 * points at "View Months" for fixing an earlier month.
 *
 * `pieColor` and the start month are fixed at creation, so both are read-only.
 */
export const EditExpenseModal = ({
  expense,
  isOpen,
  onClose,
  onSubmit,
  existingExpenses,
}: {
  /** The expense being edited; `null` keeps the modal closed. */
  expense: CustomExpenseEntity | null;
  /** Lets the parent hide the form without dropping `expense` (and with it the
   *  admin's typed input) while a save is in flight. */
  isOpen: boolean;
  onClose: () => void;
  /** Resolves once persisted; rejects to keep the form open for a retry. */
  onSubmit: (id: number, body: UpdateCustomExpenseDto) => Promise<void>;
  /** Everything already on the list, for the near-duplicate warning. This row
   *  is skipped, so an untouched name never warns about itself. */
  existingExpenses: { id: number; name: string }[];
}) => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const [name, setName] = useState('');
  const [amountCents, setAmountCents] = useState(0);
  const [hasEnd, setHasEnd] = useState(false);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [endYear, setEndYear] = useState(currentYear);

  const recurring = expense?.type === 'recurring';

  // The API rejects an end date in the past or before the start month, so the
  // pickers only offer months that are valid on both counts.
  const minYear = Math.max(currentYear, expense?.startYear ?? currentYear);
  const minMonthOfYear = (year: number) => {
    let min = 1;
    if (year === currentYear) min = Math.max(min, currentMonth);
    if (expense && year === expense.startYear)
      min = Math.max(min, expense.startMonth);
    return min;
  };

  // Reset the form to the stored values every time a different expense opens.
  useEffect(() => {
    if (!expense) return;
    setName(expense.name);
    setAmountCents(expense.amount);

    const storedEnd = expense.endMonth != null && expense.endYear != null;
    setHasEnd(storedEnd);
    // A stored end date can be in the past (it was valid when it was set); the
    // pickers can't represent that, so fall back to the earliest allowed month.
    const fallbackYear = Math.max(currentYear, expense.startYear);
    if (
      storedEnd &&
      monthIndex(expense.endYear as number, expense.endMonth as number) >=
        monthIndex(fallbackYear, minMonthOfYear(fallbackYear))
    ) {
      setEndMonth(expense.endMonth as number);
      setEndYear(expense.endYear as number);
    } else {
      setEndYear(fallbackYear);
      setEndMonth(minMonthOfYear(fallbackYear));
    }
    // Keyed on the id alone: re-running on every derived value would stomp on
    // the admin's edits mid-form. `isOpen` is deliberately not a dep either, so
    // hiding the form during a save and showing it again keeps their input.
  }, [expense?.id]);

  const years: number[] = [];
  for (let y = minYear; y <= currentYear + 5; y++) years.push(y);

  const monthsForYear = MONTHS.map((label, i) => ({
    label,
    value: i + 1,
  })).filter(({ value }) => value >= minMonthOfYear(endYear));

  // Keep the month valid when the year moves down to a partially-open year.
  const changeEndYear = (year: number) => {
    setEndYear(year);
    setEndMonth((m) => Math.max(m, minMonthOfYear(year)));
  };

  const nextMonthDate = new Date(currentYear, currentMonth, 1);
  const nextMonthLabel = monthLabel(
    nextMonthDate.getFullYear(),
    nextMonthDate.getMonth() + 1
  );

  const submit = async () => {
    if (!expense) return;

    const trimmedName = name.trim();
    if (!trimmedName) return ErrorToast('Please enter an expense name.');
    if (trimmedName.length > 120)
      return ErrorToast('Name cannot be longer than 120 characters.');
    if (amountCents < 0) return ErrorToast('Amount cannot be negative.');

    // Send only what actually changed - an empty body is a 400 ("Nothing to
    // update."), and re-sending the amount would needlessly rewrite future
    // months (and wipe any per-month overrides on them).
    const body: UpdateCustomExpenseDto = {};
    if (trimmedName !== expense.name) body.name = trimmedName;
    if (amountCents !== expense.amount) body.amount = amountCents;

    if (recurring) {
      const hadEnd = expense.endMonth != null && expense.endYear != null;
      if (hasEnd) {
        if (
          monthIndex(endYear, endMonth) <
          monthIndex(expense.startYear, expense.startMonth)
        )
          return ErrorToast('End date cannot be before the start month.');
        if (
          !hadEnd ||
          endMonth !== expense.endMonth ||
          endYear !== expense.endYear
        ) {
          body.endMonth = endMonth;
          body.endYear = endYear;
        }
      } else if (hadEnd) {
        // Both null = ongoing again.
        body.endMonth = null;
        body.endYear = null;
      }
    }

    if (Object.keys(body).length === 0) return ErrorToast('Nothing to update.');

    try {
      await onSubmit(expense.id, body);
    } catch {
      // Parent surfaced the error toast; keep the form so the admin can retry.
    }
  };

  return (
    <ModalPortal
      isOpen={isOpen && !!expense}
      closeModal={onClose}
      padding="24px"
      logo={false}
      width="560px"
      modalOverflow="auto"
      header="Edit Expense"
      // Stacks on top of the manager modal (overlay 201 / content 202) instead
      // of replacing it, so an open "View Months" list stays expanded behind.
      zIndex={203}
    >
      <Div $flex $column $gap="20px">
        <ExpenseNameField
          value={name}
          onChange={setName}
          existingExpenses={existingExpenses}
          ignoreId={expense?.id}
          warningSuffix="Keep a second line only if this is a different cost or period."
        />

        <Div>
          <AdminDollarInput2
            label="Amount"
            value={amountCents}
            onValueChange={(val: number) => setAmountCents(val)}
            $textCenter
          />
          <Disclaimer $m="0" $mt="8" $center $color={lightTheme.lightGray}>
            {recurring
              ? `Applies from ${nextMonthLabel} onward. To change an earlier month, use View Months.`
              : 'To change the amount already charged for this month, use View Months.'}
          </Disclaimer>
        </Div>

        <Div>
          <AdminColorInput
            label="Pie Color"
            color={expense?.pieColor || '#FFFFFF00'}
            onChange={() => undefined}
            disabled
            $textCenter
          />
          <Disclaimer $m="0" $mt="8" $center $color={lightTheme.lightGray}>
            The color is fixed when the expense is created.
          </Disclaimer>
        </Div>

        {recurring && (
          <>
            <Box
              $flex
              $alignCenter
              $between
              $p="0 24px"
              $height="56px"
              style={{
                boxShadow: 'none',
                border: `1px solid ${lightTheme.mediumGray}`,
              }}
            >
              <Div>
                <H5 $m="0">Has end date</H5>
                <Disclaimer $m="0" $color={lightTheme.lightGray}>
                  Off = ongoing until cancelled
                </Disclaimer>
              </Div>
              <AdminSwitch
                checked={hasEnd}
                onChange={() => setHasEnd((p) => !p)}
              />
            </Box>

            {hasEnd && (
              <Div>
                <Div $flex $gap="16px" $columnSM>
                  <Div $width="100%">
                    <AdminSelect
                      label="End Month"
                      value={endMonth}
                      onChange={(e) => setEndMonth(Number(e.target.value))}
                      options={
                        <>
                          {monthsForYear.map(({ label, value }) => (
                            <option key={label} value={value}>
                              {label}
                            </option>
                          ))}
                        </>
                      }
                      $textCenter
                    />
                  </Div>
                  <Div $width="100%">
                    <AdminSelect
                      label="End Year"
                      value={endYear}
                      onChange={(e) => changeEndYear(Number(e.target.value))}
                      options={
                        <>
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </>
                      }
                      $textCenter
                    />
                  </Div>
                </Div>
                <Disclaimer
                  $m="0"
                  $mt="8"
                  $center
                  $color={lightTheme.lightGray}
                >
                  Months after the end date are removed. An end date cannot be
                  in the past - delete an individual month instead.
                </Disclaimer>
              </Div>
            )}
          </>
        )}

        <Div $flex $gap="12px" $mt="8">
          <Button $selected $gray onClick={onClose} $w="100%">
            Cancel
          </Button>
          <Button $selected onClick={submit} $w="100%">
            Save Changes
          </Button>
        </Div>
      </Div>
    </ModalPortal>
  );
};
